






























































import { Component, Watch, Prop, Mixins } from "vue-property-decorator";
import DragNDrop, { FileInputType, FilesPayload } from "@share/common/form/form-controls/file/DragNDrop.vue";
import FormGroup from "@share/common/form/form-group/FormGroup.vue";
import SingleSelect from "@share/common/form/form-controls/select/variations/single-select/SingleSelect.vue";
import TagSelect from "@share/common/form/form-controls/select/variations/tag-select/TagSelect.vue";
import UserSelectInput from "@share/common/form/form-controls/stub/UserSelectInput.vue";
import BorderlessText from "@share/common/form/form-controls/text/BorderlessText.vue";
import CreateActions from "../PartnerCreateActions.vue";
import BounceSpinner from "@share/common/BounceSpinner.vue";
import Overlay from "@share/common/Overlay.vue";
import LangSelector from "@share/common/controls/lang-selector/LangSelector.vue";

import { UserProfile } from "@api/models/UserProfile";
import { SelectObjectSourceService } from "@share/common/services/selectControl/SelectObjectSourceService";
import { repositories } from "@service/api/Repositories";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
import { DropDownItem } from "@share/common/drop-down/types";
import { PartnerCreateModel } from "@api/models/exhibition/PartnerCreateModel";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { PartnerCreateService } from "@share/catalog/services/admin/PartnerCreateService";
import { Notice } from "@service/components/toast/type/Notice";
import { noticeActions } from "@service/store/notice/actions";
import { NoticeLevel } from "@service/components/toast/type/Enums";
import { getInvalidLangs } from "@common/form/lang";

export const getValidationObject = (langList: string[]) => {
    const langFields: { [key: string]: any } = {};

    langList.forEach((lang) => {
        langFields[`title.${lang}`] = {
            required
        }
    })

    return {
        createModel: {
            ...langFields,
            parentId: {
                required
            }
        }
    }
}

interface SearchSources {
    categories: DropDownItem[];
    tags: DropDownItem[];
}

@Component({
    components: {
        BounceSpinner, Overlay, LangSelector,
        DragNDrop, FormGroup, SingleSelect, TagSelect, UserSelectInput, BorderlessText, CreateActions
    },
    validations() {
        return getValidationObject((this as PartnerCreateForm).langList);
    }
})

export default class PartnerCreateForm extends Mixins(validationMixin) {

    @Prop({ required: true }) tagSchemaId: string;
    @Prop({ required: true }) schemaId: string;
    @Prop({ required: true }) createModel: PartnerCreateModel;
    @Prop({ required: true }) creatingService: PartnerCreateService;
    @Prop({ default: () => [] }) owners: UserProfile[];

    selectedLang = this.lang;

    FileInputType = FileInputType;

    isSaved = false;
    partnerId = "";
    loading = false;

    searchedSources: SearchSources = {
        categories: [],
        tags: []
    };

    selectedSources: SearchSources = {
        categories: [],
        tags: []
    };

    fileValue: FilesPayload = []

    showUserSelect = false;

    /**
     * SERVICES:
    */
    selectSourceService: SelectObjectSourceService = new SelectObjectSourceService(repositories.get(RepositoryFabric.OBJECT));

    mounted() {
        this.tagsSearch();
        this.categorySearch();

        this.selectedLang = this.langList[0];
    }

    async save() {
        this.touch();

        if(!this.$v.$invalid) {
            this.loading = true;

            try {
                const id = await this.creatingService.save(this.createModel, this.lang);

                this.showNotice(id);

                if(id) {
                    this.createHandler(id);
                }

                this.$emit('save');
            }
            catch(e) {
                this.showNotice();
                this.$store.dispatch('reportException', e, { root: true });
            }
            finally {
                this.loading = false;
            }
        }
    }

    createHandler(id: string) {
        this.partnerId = id;
        this.isSaved = true;

        this.fileValue.forEach(item => item.url && window.URL.revokeObjectURL(item.url))
    }

    showNotice(id?: string) {
        if (id) {
            const notice: Notice = {
                text: this.$t("exhibition.partnerSuccessfullySaved").toString(),
                level: NoticeLevel.success
            }

            this.$store.dispatch(noticeActions.submitNotice, notice);
        }
        else {
            const notice: Notice = {
                text: this.$t("exhibition.partnerSaveFailed").toString(),
                level: NoticeLevel.danger
            }

            this.$store.dispatch(noticeActions.submitNotice, notice);
        }
    }

    setFiles(payload: FilesPayload) {
        this.fileValue = payload;

        this.createModel.iconFile = payload[0]?.file;
    }

    async categorySearch(keyword = "") {
        this.searchedSources.categories = await this.selectSourceService?.getSelectSource({ keyword, schemaId: this.schemaId, onlyParents: true, eventId: this.eventId, keywordLength: 0 }) || [];
    }

    async tagsSearch(keyword = "") {
        this.searchedSources.tags = await this.selectSourceService?.getSelectSource({ keyword, schemaId: this.tagSchemaId, withoutParents: true, keywordLength: 0 }) || [];
    }

    touch() {
        this.$v.$touch();
    }

    @Watch('owners')
    watchOwners(owners: UserProfile[]) {
        this.createModel.userProfileIds = owners.map(owner => owner.id)
        this.createModel.ownerIds = owners
            .map(owner => owner.userId)
            .filter(userId => !!userId || userId === 0) as number[];
    }

    /**
     * VALIDATION MODEL
     */
    get vCreateModel() {
        return this.$v.createModel
    }

    get $vTitle() {
        return this.vCreateModel[`title.${this.selectedLang}`];
    }

    // TITLE:
    get $vTitleModel() {
        return this.$vTitle?.$model
    }

    set $vTitleModel(value: string) {
        if(this.$vTitle) {
            this.$vTitle.$model = value;
        }
    }

    get langList(): string[] {
        return this.creatingService?.languages || [];
    }

    /* Categories */
    get $vCategory() {
        return this.vCreateModel.parentId;
    }

    get categoryId(): DropDownItem {
        return this.selectedSources.categories.find((item) => item.id === this.vCreateModel.parentId?.$model) || { title: "", id: this.vCreateModel.parentId?.$model }
    }

    set categoryId(value: DropDownItem) {
        this.selectedSources.categories = [value];

        if(this.vCreateModel.parentId) {
            this.vCreateModel.parentId.$model = value.id;
        }
    }

    /* Tags */
    get tagsIds(): DropDownItem[] {
        return this.selectedSources.tags.filter((item) => this.createModel?.tagsIds?.includes(item.id || ""))
    }

    set tagsIds(value: DropDownItem[]) {
        this.selectedSources.tags = value || [];

        if(value) {
            this.createModel.tagsIds = value.map((item) => item.id || "") || [];
        }
    }

    get blockNoLangFields() {
        return this.selectedLang !== (this.creatingService?.languages?.[0] || 'en')
    }

    get categoriesSource() {
        return this.searchedSources.categories;
    }

    get tagsSource() {
        return this.searchedSources.tags;
    }

    get invalidlangs() {
        return getInvalidLangs(this.$v.createModel);
    }

    get ownersName() {
        return this.owners.map((owner) => owner.fullName).join(', ')
    }

    get lang() {
        return this.$store.state.session?.language || navigator.language.split('-')[0] || "en";
    }

    get eventId(): string {
        return this.$store.state.events.selected;
    }
}

